import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './contexts/AuthContext';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import ScrollToTop from './components/ScrollToTop';
import AuthErrorBoundary from './components/AuthErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';

// Components that are needed immediately
import Login from './pages/auth/Login';
import ResetPassword from './pages/auth/ResetPassword';
import Layout from './pages/Layout';

// Lazy load all other components
const LandingPage = React.lazy(() => import('./LandingPage'));
const PDFInvoiceMagnet = React.lazy(() => import('./pages/invoice_OCR/PDFInvoiceMagnet'));
const InvoiceProcessor = React.lazy(() => import('./pages/gmail_invoice_processor/InvoiceProcessor'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const Terms = React.lazy(() => import('./pages/Terms'));
const InvoiceRulesNL = React.lazy(() => import('./pages/gmail_invoice_processor/Dutch/InvoiceRulesNL'));
const FactuurRegelsNL = React.lazy(() => import('./pages/gmail_invoice_processor/Dutch/FactuurRegelsNL'));
const InvoiceGenerator = React.lazy(() => import('./pages/invoice_generator/InvoiceGenerator'));
const CAMT053Guide = React.lazy(() => import('./pages/gmail_invoice_processor/CAMT053Guide'));
const MT940Guide = React.lazy(() => import('./pages/gmail_invoice_processor/MT940Guide'));
const EInvoicingGuide = React.lazy(() => import('./pages/gmail_invoice_processor/EInvoicingGuideNL'));
const EFacturatieGidsNL = React.lazy(() => import('./pages/gmail_invoice_processor/Dutch/EFacturatieGidsNL'));
const Profile = React.lazy(() => import('./pages/auth/Profile'));
const PdfInvoiceProcessor = React.lazy(() => import('./pages/invoice_OCR/PdfInvoiceProcessor'));
const PurchaseSuccess = React.lazy(() => import('./pages/PurchaseSuccess'));
const PurchaseCancelled = React.lazy(() => import('./pages/PurchaseCancelled'));
const SerenityPrivacyPolicy = React.lazy(() => import('./pages/SerenityPrivacyPolicy'));
const KanbanPrivacyPolicy = React.lazy(() => import('./pages/KanbanPrivacyPolicy'));
const DeepBoard = React.lazy(() => import('./pages/DeepBoard'));
const DataCleansingServices = React.lazy(() => import('./pages/DataCleansingServices'));
const ExcelDuplicatesGuide = React.lazy(() => import('./pages/guides/ExcelDuplicatesGuide'));
const ExcelDataCleaningGuide = React.lazy(() => import('./pages/guides/ExcelDataCleaningGuide'));
const ExcelTextFunctionsGuide = React.lazy(() => import('./pages/guides/ExcelTextFunctionsGuide'));
const DataCleanupGuide = React.lazy(() => import('./pages/guides/DataCleanupGuide'));
const GoogleSheetsDuplicatesGuide = React.lazy(() => import('./pages/guides/GoogleSheetsDuplicatesGuide'));
const CRMDataHygieneGuide = React.lazy(() => import('./pages/guides/CRMDataHygieneGuide'));
const AddressCleansingGuide = React.lazy(() => import('./pages/guides/AddressCleansingGuide'));
const PowerQueryDuplicatesGuide = React.lazy(() => import('./pages/guides/PowerQueryDuplicatesGuide'));
const DataQualityAssessmentGuide = React.lazy(() => import('./pages/guides/DataQualityAssessmentGuide'));
const DuplicateRemovalTool = React.lazy(() => import('./pages/DuplicateRemovalTool'));
const Tools = React.lazy(() => import('./pages/Tools'));
const SalesAnalyticsTool = React.lazy(() => import('./pages/SalesAnalyticsTool'));
const BankStatementConverter = React.lazy(() => import('./pages/BankStatementConverter'));

// Dashboard components
const DashboardLayout = React.lazy(() => import('./components/dashboard/DashboardLayout'));
const DashboardOverview = React.lazy(() => import('./pages/dashboard/DashboardOverview'));
const DashboardInvoiceProcessor = React.lazy(() => import('./pages/dashboard/DashboardInvoiceProcessor'));
const DashboardInvoiceGenerator = React.lazy(() => import('./pages/dashboard/DashboardInvoiceGenerator'));
const Settings = React.lazy(() => import('./pages/dashboard/Settings'));
const DashboardInvoices = React.lazy(() => import('./pages/dashboard/DashboardInvoices'));
const DashboardBankStatements = React.lazy(() => import('./pages/dashboard/DashboardBankStatements'));

// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
  </div>
);

// Get Google Client ID from environment variable
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const App: React.FC = () => {
  if (!GOOGLE_CLIENT_ID) {
    console.error('Google Client ID is not configured. Please set REACT_APP_GOOGLE_CLIENT_ID in your environment variables.');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg">
          <h1 className="text-xl font-bold text-red-600 mb-4">Configuration Error</h1>
          <p className="text-gray-600">
            Google Client ID is not configured. Please check your environment variables.
          </p>
        </div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <GoogleOAuthProvider 
        clientId={GOOGLE_CLIENT_ID}
        onScriptLoadError={() => console.error('Google OAuth script failed to load')}
        onScriptLoadSuccess={() => console.log('Google OAuth script loaded successfully')}
      >
        <Router>
          <ScrollToTop />
          <AuthErrorBoundary>
            <AuthProvider>
              <AnalyticsProvider>
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route 
                      path="/login" 
                      element={
                        <ProtectedRoute requireAuth={false}>
                          <Login />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/reset-password" 
                      element={
                        <ProtectedRoute requireAuth={false}>
                          <ResetPassword />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/profile" 
                      element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/purchase-success" 
                      element={
                        <ProtectedRoute>
                          <PurchaseSuccess />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/purchase-cancelled" 
                      element={
                        <ProtectedRoute>
                          <PurchaseCancelled />
                        </ProtectedRoute>
                      } 
                    />
                    
                    {/* Privacy Policy Pages */}
                    <Route
                      path="/serenity-privacy-policy"
                      element={<SerenityPrivacyPolicy />}
                    />
                    <Route
                      path="/kanban-privacy-policy"
                      element={<KanbanPrivacyPolicy />}
                    />

                    {/* DeepBoard Promotional Page */}
                    <Route
                      path="/deepboard"
                      element={<DeepBoard />}
                    />
                    
                    {/* Dashboard Routes - Entrepreneur's Cabinet */}
                    <Route 
                      path="/dashboard" 
                      element={
                        <ProtectedRoute>
                          <DashboardLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<DashboardOverview />} />
                      <Route path="invoices" element={<DashboardInvoices />} />
                      <Route
                        path="processor"
                        element={<DashboardInvoiceProcessor />}
                      />
                      <Route
                        path="generator"
                        element={<DashboardInvoiceGenerator />}
                      />
                      <Route
                        path="bank-statements"
                        element={<DashboardBankStatements />}
                      />
                      <Route
                        path="settings"
                        element={<Settings />}
                      />
                    </Route>
                    
                    <Route path="/tools" element={<Layout><Tools /></Layout>} />
                    <Route path="/bank-statement-converter" element={<Layout><BankStatementConverter /></Layout>} />
                    <Route path="/invoice-generator" element={<Layout><InvoiceGenerator /></Layout>} />
                    <Route path="/data-cleansing-services" element={<Layout><DataCleansingServices /></Layout>} />
                    <Route path="/excel-remove-duplicates-guide" element={<Layout><ExcelDuplicatesGuide /></Layout>} />
                    <Route path="/excel-data-cleaning-guide" element={<Layout><ExcelDataCleaningGuide /></Layout>} />
                    <Route path="/excel-text-functions-guide" element={<Layout><ExcelTextFunctionsGuide /></Layout>} />
                    <Route path="/data-cleanup-checklist" element={<Layout><DataCleanupGuide /></Layout>} />
                    <Route path="/google-sheets-remove-duplicates-guide" element={<Layout><GoogleSheetsDuplicatesGuide /></Layout>} />
                    <Route path="/crm-data-hygiene-guide" element={<Layout><CRMDataHygieneGuide /></Layout>} />
                    <Route path="/address-cleansing-guide" element={<Layout><AddressCleansingGuide /></Layout>} />
                    <Route path="/power-query-remove-duplicates-guide" element={<Layout><PowerQueryDuplicatesGuide /></Layout>} />
                    <Route path="/when-you-need-professional-data-cleaning" element={<Layout><DataQualityAssessmentGuide /></Layout>} />
                    <Route path="/duplicate-removal-tool" element={<Layout><DuplicateRemovalTool /></Layout>} />
                    <Route path="/invoice_processor" element={<Layout />}>
                      <Route index element={<InvoiceProcessor />} />
                      <Route path="privacy" element={<Privacy />} />
                      <Route path="tos" element={<Terms />} />
                      <Route path="invoice-rules-nl" element={<InvoiceRulesNL />} />
                      <Route path="factuur-regels-nl" element={<FactuurRegelsNL />} />
                      <Route path="camt053-guide" element={<CAMT053Guide />} />
                      <Route path="mt940-guide" element={<MT940Guide />} />
                      <Route path="e-invoicing-guide" element={<EInvoicingGuide />} />
                      <Route path="e-facturatie-gids-nl" element={<EFacturatieGidsNL />} />
                      <Route 
                        path="pdf" 
                        element={
                          <ProtectedRoute>
                            <PdfInvoiceProcessor />
                          </ProtectedRoute>
                        } 
                      />
                    </Route>

                    {/* High Accuracy Invoice OCR - New URL */}
                    <Route path="/invoice-ocr" element={<Layout />}>
                      <Route index element={<PDFInvoiceMagnet />} />
                    </Route>

                    {/* Legacy redirect: PDF Invoice Magnet to new Invoice OCR URL */}
                    <Route path="/pdf-invoice-magnet" element={<Navigate to="/invoice-ocr" replace />} />

                    {/* Redirect old routes to new top-level routes */}
                    <Route path="/invoice_processor/invoice-generator" element={<Navigate to="/invoice-generator" replace />} />
                    
                    {/* SEO redirect: Ensure pdf-invoice-magnet redirects to invoice-ocr */}
                    <Route path="/pdf-invoice-magnet/*" element={<Navigate to="/invoice-ocr" replace />} />
                    
                    {/* Redirect users to dashboard if they try to access protected features directly */}
                    <Route path="/cabinet" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/entrepreneur" element={<Navigate to="/dashboard" replace />} />

                    {/* Sales Analytics Tool */}
                    <Route path="/sales-analytics" element={<SalesAnalyticsTool />} />
                  </Routes>
                </Suspense>
              </AnalyticsProvider>
            </AuthProvider>
          </AuthErrorBoundary>
        </Router>
      </GoogleOAuthProvider>
    </HelmetProvider>
  );
};

export default App;